// Unless explicitly stated otherwise all files in this repository are licensed under the MIT License.
// This product includes software developed at Datadog (https://www.datadoghq.com/).
// Copyright 2019-Present Datadog, Inc.

// This file is partially generated.
// Anything between #imports-injection-marker and #types-injection-marker
// will be updated using the 'yarn cli integrity' command.
import type { TrackedFilesMatcher } from '@dd/internal-git-plugin/trackedFilesMatcher';
/* eslint-disable arca/import-ordering */
// #imports-injection-marker
import type { AppsOptions } from '@dd/apps-plugin/types';
import type * as apps from '@dd/apps-plugin';
import type { ErrorTrackingOptions } from '@dd/error-tracking-plugin/types';
import type * as errorTracking from '@dd/error-tracking-plugin';
import type { LiveDebuggerOptions } from '@dd/live-debugger-plugin/types';
import type * as liveDebugger from '@dd/live-debugger-plugin';
import type { MetricsOptions } from '@dd/metrics-plugin/types';
import type * as metrics from '@dd/metrics-plugin';
import type { OutputOptions } from '@dd/output-plugin/types';
import type * as output from '@dd/output-plugin';
import type { RumOptions } from '@dd/rum-plugin/types';
import type * as rum from '@dd/rum-plugin';
// #imports-injection-marker
/* eslint-enable arca/import-ordering */
import type { BodyInit } from 'undici-types';
import type { UnpluginOptions } from 'unplugin';

import type { ALL_ENVS, SITES, SUPPORTED_BUNDLERS } from './constants';

export type Assign<A, B> = Omit<A, keyof B> & B;
export type WithRequired<T, K extends keyof T> = T & { [P in K]-?: T[P] };
export type IterableElement<IterableType extends Iterable<unknown>> =
    IterableType extends Iterable<infer ElementType> ? ElementType : never;

export interface RepositoryData {
    commit: {
        hash: string;
        message: string;
        author: {
            name: string;
            email: string;
            date: string;
        };
        committer: {
            name: string;
            email: string;
            date: string;
        };
    };
    hash: string;
    branch: string;
    remote: string;
    trackedFilesMatcher: TrackedFilesMatcher;
}

export type FileReport = { filepath: string; name: string; size: number; type: string };
export type Input = FileReport & { dependencies: Set<Input>; dependents: Set<Input> };
export type Output = FileReport & { inputs: (Input | Output)[] };
export type Entry = Output & { outputs: Output[] };

export type SerializedEntry = Assign<Entry, { inputs: string[]; outputs: string[] }>;
export type SerializedInput = Assign<Input, { dependencies: string[]; dependents: string[] }>;
export type SerializedOutput = Assign<Output, { inputs: string[] }>;

export interface Metric {
    metric: string;
    type: 'count' | 'size' | 'duration';
    points: [number, number][];
    tags: string[];
}

export interface MetricToSend extends Metric {
    toSend: boolean;
}

export type Log = {
    bundler?: BundlerName;
    pluginName: string;
    type: LogLevel;
    message: string;
    time: number;
};
export type LogTags = string[];
export type Timer = {
    label: string;
    pluginName: string;
    spans: { start: number; end?: number; tags: LogTags }[];
    tags: LogTags;
    total: number;
    logLevel: LogLevel;
};

export type BuildMetadata = {
    name?: string;
    version?: string;
};

export type BuildReport = {
    bundler: GlobalData['bundler'];
    errors: GlobalStores['errors'];
    warnings: GlobalStores['warnings'];
    logs: GlobalStores['logs'];
    timings: GlobalStores['timings'];
    metadata: GlobalData['metadata'];
    entries?: Entry[];
    inputs?: Input[];
    outputs?: Output[];
    start?: number;
    end?: number;
    duration?: number;
    writeDuration?: number;
};

// A JSON safe version of the report.
export type SerializedBuildReport = Assign<
    BuildReport,
    {
        entries: SerializedEntry[];
        inputs: SerializedInput[];
        outputs: SerializedOutput[];
    }
>;

export type BundlerName = (typeof SUPPORTED_BUNDLERS)[number];
export type BundlerReport = GlobalData['bundler'] & {
    outDir: string;
    rawConfig?: any;
};

export type ChunkInfo = {
    sourceOrHash: string;
    fileName: string;
    isEntry: boolean;
};

// Static string, lazy async loader (e.g. file fetch), or per-chunk code generator.
export type InjectedValue = string | (() => Promise<string>) | ((chunk?: ChunkInfo) => string);

export enum InjectPosition {
    BEFORE,
    MIDDLE,
    AFTER,
}

export type ToInjectItem = {
    type: 'file' | 'code';
    value: InjectedValue;
    fallback?: ToInjectItem;
} & (
    | { position?: InjectPosition.BEFORE | InjectPosition.AFTER; injectIntoAllChunks?: boolean }
    | { position: InjectPosition.MIDDLE }
);

export type TimeLogger = {
    timer: Timer;
    resume: (startTime?: number) => void;
    end: (endTime?: number) => void;
    pause: (pauseTime?: number, warn?: boolean) => void;
    tag: (tags: LogTags, opts?: { span?: boolean }) => void;
};

// The rest parameter is a LogLevel or a boolean to auto start the timer.
export type TimeLog = (
    label: string,
    opts?: { level?: LogLevel; start?: boolean | number; log?: boolean; tags?: LogTags },
) => TimeLogger;
export type GetLogger = (name: string) => Logger;
export type LogOptions = { forward?: boolean; context?: LogData };
export type LoggerFn = (text: any, opts?: LogOptions) => void;
export type Logger = {
    getLogger: GetLogger;
    time: TimeLog;
    error: LoggerFn;
    warn: LoggerFn;
    info: LoggerFn;
    debug: LoggerFn;
};
type RestContext = string | string[] | number | boolean;
export type LogData = Record<string, RestContext | Record<string, RestContext>>;
export type DdLogOptions = {
    message: string;
    context?: LogData;
};
export type Env = (typeof ALL_ENVS)[number];
export type TriggerHook<R> = <K extends keyof CustomHooks>(
    name: K,
    ...args: Parameters<NonNullable<CustomHooks[K]>>
) => R;
export type GlobalContext = {
    addMetric: (metric: Metric) => void;
    artifactsPending: boolean;
    artifactsReady: Promise<void>;
    asyncHook: TriggerHook<Promise<void[]>>;
    auth: AuthOptionsWithDefaults;
    build: BuildReport;
    bundler: BundlerReport;
    buildRoot: string;
    env: GlobalData['env'];
    getLogger: GetLogger;
    git?: RepositoryData;
    hook: TriggerHook<void>;
    inject: (item: ToInjectItem) => void;
    markArtifactsPending: () => void;
    markArtifactsReady: (error?: unknown) => void;
    pluginNames: string[];
    plugins: (PluginOptions | CustomPluginOptions)[];
    queue: (promise: Promise<any>) => void;
    sendLog: (args: DdLogOptions) => Promise<void>;
    start: number;
    version: GlobalData['version'];
};

export type FactoryMeta = {
    bundler: any;
    version: string;
};

export type HookFn<T extends Array<any>> = (...args: T) => void;
export type AsyncHookFn<T extends Array<any>> = (...args: T) => Promise<void> | void;
export type CustomHooks = {
    asyncTrueEnd?: () => Promise<void> | void;
    buildRoot?: HookFn<[string]>;
    flush?: () => Promise<void> | void;
    init?: HookFn<[GlobalContext]>;
    buildReport?: AsyncHookFn<[BuildReport]>;
    bundlerReport?: HookFn<[BundlerReport]>;
    git?: AsyncHookFn<[RepositoryData]>;
    metrics?: AsyncHookFn<[Set<Metric>]>;
    syncTrueEnd?: () => void;
    timings?: AsyncHookFn<[TimingsReport]>;
};

export type PluginOptions = Assign<
    UnpluginOptions & CustomHooks,
    {
        name: PluginName;
    }
>;

export type CustomPluginOptions = Assign<
    PluginOptions,
    {
        name: string;
    }
>;

export type GetPluginsArg = {
    bundler: any;
    context: GlobalContext;
    options: OptionsWithDefaults;
    data: GlobalData;
    stores: GlobalStores;
};
export type GetPlugins = (arg: GetPluginsArg) => PluginOptions[];
export type GetCustomPlugins = (arg: GetPluginsArg) => CustomPluginOptions[];
export type GetInternalPlugins = (arg: GetPluginsArg) => PluginOptions[];
export type GetWrappedPlugins = (arg: GetPluginsArg) => (PluginOptions | CustomPluginOptions)[];

export type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'none';

export type Site = (typeof SITES)[number];
export type AuthOptions = {
    apiKey?: string;
    appKey?: string;
    site?: string;
};

export type AuthOptionsWithDefaults = Omit<AuthOptions, 'site'> & {
    site: Site;
};

export interface BaseOptions {
    auth?: AuthOptions;
    metadata?: BuildMetadata;
    enableGit?: boolean;
    logLevel?: LogLevel;
}

type SourcemapsUploadEnabledOptions = {
    upload: true;
    bailOnError?: boolean;
    dryRun?: boolean;
    maxConcurrency?: number;
};

type SourcemapsInjectionOnlyOptions = {
    upload?: false;
    bailOnError?: never;
    dryRun?: never;
    maxConcurrency?: never;
};

export type SourcemapsOptions = {
    debugId: true;
} & (SourcemapsUploadEnabledOptions | SourcemapsInjectionOnlyOptions);

export interface Options extends BaseOptions {
    // Each product should have a unique entry.
    // #types-injection-marker
    [apps.CONFIG_KEY]?: AppsOptions;
    [errorTracking.CONFIG_KEY]?: ErrorTrackingOptions;
    [liveDebugger.CONFIG_KEY]?: LiveDebuggerOptions;
    [metrics.CONFIG_KEY]?: MetricsOptions;
    [output.CONFIG_KEY]?: OutputOptions;
    [rum.CONFIG_KEY]?: RumOptions;
    // #types-injection-marker
    sourcemaps?: SourcemapsOptions;
    /** Repository URL used in Git metadata instead of the detected remote URL. */
    gitRepositoryUrl?: string;
    customPlugins?: GetCustomPlugins;
}

export type OptionsWithDefaults = Assign<
    Assign<Options, Required<BaseOptions>>,
    { auth: AuthOptionsWithDefaults }
>;

export type PluginName = `datadog-${Lowercase<string>}-plugin`;

export type ApiKeyRequestAuthOptions = {
    apiKey?: string;
    appKey?: string;
};

export type OAuthRequestAuthOptions = {
    accessToken: string;
};

// Request-local auth. OAuth callers must resolve tokens before calling doRequest.
export type RequestAuthOptions = ApiKeyRequestAuthOptions | OAuthRequestAuthOptions;

type Data = { data?: BodyInit; headers?: Record<string, string> };
export type RequestOpts = {
    url: string;
    auth?: RequestAuthOptions;
    method?: string;
    getData?: () => Promise<Data> | Data;
    type?: 'json' | 'text';
    onRetry?: (error: Error, attempt: number) => void;
    // Invoked with the raw response of each successful (HTTP-ok) attempt, before the
    // body is consumed. Useful to read response headers (e.g. ETag, Last-Modified).
    onResponse?: (response: Response) => void;
    retries?: number;
    minTimeout?: number;
    maxTimeout?: number;
    signal?: AbortSignal;
    // Defaults to the global at call time; pass a captured reference to ignore later reassignment.
    fetchImpl?: typeof fetch;
};

export type ResolvedEntry = { name?: string; resolved: string; original: string };

export interface LocalAppendOptions {
    contentType: string;
    filename: string;
}

export type FileValidity = {
    empty: boolean;
    exists: boolean;
};

export type GlobalData = {
    bundler: {
        name: BundlerName;
        version: string;
    };
    env: Env;
    metadata: BuildMetadata;
    packageName: string;
    version: string;
};

export type GlobalStores = {
    errors: string[];
    logs: Log[];
    metrics: Set<Metric>;
    queue: Promise<any>[];
    timings: Timer[];
    warnings: string[];
};

/* Legacy Telemetry types */
export type TAP_TYPES = 'default' | 'async' | 'promise';

export interface ValueContext {
    type: string;
    name: string;
    value?: string;
}

export interface Value {
    start: number;
    end: number;
    duration: number;
    context?: ValueContext[];
    type?: TAP_TYPES; // Only for webpack.
}

export interface Timing {
    name: string;
    duration: number;
    increment: number;
    events: {
        [key: string]: {
            name: string;
            values: Value[];
        };
    };
}
export type TimingsMap = Map<string, Timing>;

export interface TimingsReport {
    tapables?: TimingsMap;
    loaders?: TimingsMap;
    modules?: TimingsMap;
}
